import { useState, useEffect } from '@wordpress/element';
import { useDispatch, useSelect } from '@wordpress/data';
import { TabPanel } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import SettingsTab from './components/SettingsTab';
import StylingTab from './components/StylingTab';
import DisplayRulesTab from './components/DisplayRulesTab';
import '../store';

const App = () => {
	const postSettingsData = window.topbuddyPostSettings || {};
	const { 
		availableComponents = {}, 
		initialComponents = [], 
		initialSettings = {},
		initialDisplayRules = {},
		displayRulesData = {}
	} = postSettingsData;

	// Components state lifted to App level to persist across tab switches.
	const [activeComponents, setActiveComponents] = useState(initialComponents);

	// Settings state.
	const [settings, setSettings] = useState({
		isActive: initialSettings.isActive || false,
		position: initialSettings.position || 'top',
		isSticky: initialSettings.isSticky || false,
		backgroundColor: initialSettings.backgroundColor || '#f7f7f7',
		backgroundImage: initialSettings.backgroundImage || '',
		backgroundGradient: initialSettings.backgroundGradient || '',
		backgroundOverlayOpacity: initialSettings.backgroundOverlayOpacity !== undefined ? initialSettings.backgroundOverlayOpacity : 0.6,
		barWidth: initialSettings.barWidth || '1200px',
		alignment: initialSettings.alignment || 'center',
		enableCloseButton: initialSettings.enableCloseButton !== false,
		showAgainAfterDays: initialSettings.showAgainAfterDays ?? 7,
		enableBarDelay: initialSettings.enableBarDelay || false,
		barDelaySeconds: initialSettings.barDelaySeconds ?? 5,
		padding: initialSettings.padding || 'medium',
		closeColor: initialSettings.closeColor || '#000000',
		closeHoverColor: initialSettings.closeHoverColor || '#555555',
		closeBgColor: initialSettings.closeBgColor || '#ffffff',
		closeHoverBgColor: initialSettings.closeHoverBgColor || '#ffffff',
		deviceVisibility: initialSettings.deviceVisibility || 'everywhere',
		breakpoint: initialSettings.breakpoint ?? 768,
	});

	// Get dispatch for settings and components stores.
	const { setSettings: setStoreSettings } = useDispatch('topbuddy/settings');
	const { setActiveComponents: setStoreComponents, setAvailableComponents: setStoreAvailableComponents } = useDispatch('topbuddy/components');
	const { setDisplayRules: setStoreDisplayRules } = useDispatch('topbuddy/displayRules');
	const { clearTabRequest } = useDispatch('topbuddy/ui');

	// Subscribe to UI store for tab switch requests and components store
	const { requestedTab, storeActiveComponents, storeSettings, storeDisplayRules } = useSelect((select) => {
		return {
			requestedTab: select('topbuddy/ui')?.getRequestedTab(),
			storeActiveComponents: select('topbuddy/components')?.getActiveComponents() || [],
			storeSettings: select('topbuddy/settings')?.getSettings() || null,
			storeDisplayRules: select('topbuddy/displayRules')?.getDisplayRules() || null,
		};
	}, []);

	// Initialize store with settings and available components on mount.
	useEffect(() => {
		setStoreSettings(settings);
		setStoreAvailableComponents(availableComponents);
		setStoreDisplayRules(rules);
	}, []);

	// Sync activeComponents to hidden input and store whenever they change.
	useEffect(() => {
		const hiddenInput = document.getElementById('topbuddy-components-data');
		if (hiddenInput) {
			hiddenInput.value = JSON.stringify(activeComponents);
		}
		// Sync to store for preview
		setStoreComponents(activeComponents);
	}, [activeComponents, setStoreComponents]);

	// Sync store changes back to local state (when preview edits components)
	useEffect(() => {
		if (storeActiveComponents && storeActiveComponents.length >= 0) {
			const storeJson = JSON.stringify(storeActiveComponents);
			const localJson = JSON.stringify(activeComponents);
			
			// Only update if store is different from local state
			if (storeJson !== localJson) {
				setActiveComponents(storeActiveComponents);
				
				// Also update hidden input directly
				const hiddenInput = document.getElementById('topbuddy-components-data');
				if (hiddenInput) {
					hiddenInput.value = storeJson;
				}
			}
		}
	}, [storeActiveComponents]);

	// Handle tab switch requests from store
	useEffect(() => {
		if (requestedTab) {
			// Find the tab button and click it
			const tabButton = document.querySelector(`[id*="tab-panel"][id*="${requestedTab}"]`);
			if (tabButton && !tabButton.classList.contains('is-active')) {
				tabButton.click();
			}
			// Clear the request
			clearTabRequest();
		}
	}, [requestedTab, clearTabRequest]);

	// Add back to dashboard link inside the tabs sidebar
	useEffect(() => {
		const tabsContainer = document.querySelector('.topbuddy-tab-panel .components-tab-panel__tabs');
		if (tabsContainer && !document.querySelector('.topbuddy-back-to-dashboard')) {
			const dashboardLink = document.createElement('a');
			dashboardLink.href = 'options-general.php?page=topbuddy-dashboard';
			dashboardLink.className = 'topbuddy-back-to-dashboard';
			dashboardLink.innerHTML = '<span class="dashicons dashicons-arrow-left-alt2"></span>' + __('Back to dashboard', 'topbuddy');
			tabsContainer.appendChild(dashboardLink);
		}
	}, []);

	// Update hidden input and store whenever settings change.
	useEffect(() => {
		const hiddenInput = document.getElementById('topbuddy-settings-data');
		if (hiddenInput) {
			hiddenInput.value = JSON.stringify(settings);
		}
		// Update store for live preview.
		setStoreSettings(settings);
	}, [settings, setStoreSettings]);

	// Sync store settings changes back to local state (when template imported)
	useEffect(() => {
		if (storeSettings && Object.keys(storeSettings).length > 0) {
			const storeJson = JSON.stringify(storeSettings);
			const localJson = JSON.stringify(settings);
			
			// Only update if store is different from local state
			if (storeJson !== localJson) {
				setSettings(storeSettings);
				
				// Also update hidden input directly
				const hiddenInput = document.getElementById('topbuddy-settings-data');
				if (hiddenInput) {
					hiddenInput.value = storeJson;
				}
			}
		}
	}, [storeSettings]);

	// Update a specific setting.
	const updateSetting = (key, value) => {
		setSettings(prevSettings => ({
			...prevSettings,
			[key]: value,
		}));
	};

	// Display rules state.
	const [rules, setRules] = useState({
		user_login_status: initialDisplayRules.user_login_status || 'any',
		user_roles: initialDisplayRules.user_roles || [],
		display_location: initialDisplayRules.display_location || 'everywhere',
		general_pages: initialDisplayRules.general_pages || [],
		specific_pages: initialDisplayRules.specific_pages || [],
		schedule_type: initialDisplayRules.schedule_type || 'always',
		schedule_start: initialDisplayRules.schedule_start || null,
		schedule_end: initialDisplayRules.schedule_end || null,
		schedule_days: initialDisplayRules.schedule_days || [],
		// Advanced Display Rules (Pro)
		referrer_enabled: initialDisplayRules.referrer_enabled || false,
		referrer_url: initialDisplayRules.referrer_url || '',
		query_params_enabled: initialDisplayRules.query_params_enabled || false,
		query_params: initialDisplayRules.query_params || [],
		page_views_enabled: initialDisplayRules.page_views_enabled || false,
		page_views_operator: initialDisplayRules.page_views_operator || 'greater_than',
		page_views_count: initialDisplayRules.page_views_count || 1,
		time_on_site_enabled: initialDisplayRules.time_on_site_enabled || false,
		time_on_site_seconds: initialDisplayRules.time_on_site_seconds || 30,
		cookie_enabled: initialDisplayRules.cookie_enabled || false,
		cookie_name: initialDisplayRules.cookie_name || '',
		cookie_value: initialDisplayRules.cookie_value || '',
	});

	// Update hidden input whenever rules change.
	useEffect(() => {
		const hiddenInput = document.getElementById('topbuddy-display-rules-data');
		if (hiddenInput) {
			hiddenInput.value = JSON.stringify(rules);
		}
		// Update store for live preview.
		setStoreDisplayRules(rules);
	}, [rules, setStoreDisplayRules]);

	// Sync store display rules changes back to local state (when template imported)
	useEffect(() => {
		if (storeDisplayRules && Object.keys(storeDisplayRules).length > 0) {
			const storeJson = JSON.stringify(storeDisplayRules);
			const localJson = JSON.stringify(rules);
			
			// Only update if store is different from local state
			if (storeJson !== localJson) {
				setRules(storeDisplayRules);
				
				// Also update hidden input directly
				const hiddenInput = document.getElementById('topbuddy-display-rules-data');
				if (hiddenInput) {
					hiddenInput.value = storeJson;
				}
			}
		}
	}, [storeDisplayRules]);

	// Update a specific rule.
	const updateRule = (key, value) => {
		setRules(prevRules => ({
			...prevRules,
			[key]: value,
		}));
	};
	const tabs = [
		{
			name: 'settings',
			title: (
				<>
					<span className="dashicons dashicons-admin-generic"></span>
					{__('General', 'topbuddy')}
				</>
			),
			className: 'topbuddy-tab-settings',
		},
		{
			name: 'display-rules',
			title: (
				<>
					<span className="dashicons dashicons-visibility"></span>
					{__('Display Rules', 'topbuddy')}
				</>
			),
			className: 'topbuddy-tab-display-rules',
		},
	];

	// Settings sub-tabs
	const settingsTabs = [
		{
			name: 'general-settings',
			title: __('Settings', 'topbuddy'),
		},
		{
			name: 'styling',
			title: __('Styling', 'topbuddy'),
		},
	];

	return (
		<div className="topbuddy-settings-tabs">
			<TabPanel
				className="topbuddy-tab-panel"
				activeClass="is-active"
				orientation="vertical"
				tabs={tabs}
				initialTabName="settings"
			>
				{(tab) => (
					<div className="topbuddy-tab-content">
						{tab.name === 'settings' && (
							<div className="topbuddy-tab-panel-content">
								<TabPanel
									className="topbuddy-settings-nested-tabs"
									activeClass="is-active"
									tabs={settingsTabs}
									initialTabName="general-settings"
								>
									{(settingsTab) => (
										<div className="topbuddy-settings-tab-wrapper">
											{settingsTab.name === 'general-settings' && (
												<SettingsTab
													settings={settings}
													updateSetting={updateSetting}
												/>
											)}
											{settingsTab.name === 'styling' && (
												<StylingTab
													settings={settings}
													updateSetting={updateSetting}
												/>
											)}
										</div>
									)}
								</TabPanel>
							</div>
						)}
					{tab.name === 'display-rules' && (
						<div className="topbuddy-tab-panel-content">
							<DisplayRulesTab
								rules={rules}
								updateRule={updateRule}
								displayRulesData={displayRulesData}
							/>
							</div>
						)}
					</div>
				)}
			</TabPanel>
		</div>
	);
};

export default App;
